import { useMemo } from "react"

import { useTransportQuery, useTransportQueryUtils } from "@/transport"

import type { TransportOperationRequest } from "@/types_api/backend/transport/operations"
import { normalizeConversationTransportRow } from "@/utils/conversationsTransportRows"

type ConversationKeysetRequest =
  TransportOperationRequest<"queryUploadedConversationKeyset">

const QUERY_UPLOADED_CONVERSATION_KEYSET =
  "queryUploadedConversationKeyset" as const
const TRANSCRIPT_PAGE_SIZE = 25

interface MetricValueFilterRequest {
  metricId: string
  operator: ">" | "<" | "=" | ">=" | "<="
  value: string
  valueType: "float" | "string"
}

export function useRecentTranscriptsTransport({
  activeMetricValueFilters,
  includeAnyResultMetric,
  metricId,
  monitoringFilter,
}: {
  activeMetricValueFilters?: MetricValueFilterRequest[]
  includeAnyResultMetric: boolean
  metricId?: string
  monitoringFilter: boolean
}) {
  const request = useMemo(
    (): ConversationKeysetRequest => ({
      limit: TRANSCRIPT_PAGE_SIZE,
      filters: {
        monitoring_filter: monitoringFilter,
        metric_ids: includeAnyResultMetric && metricId ? [metricId] : undefined,
        metric_value_filters: activeMetricValueFilters?.map((filter) => ({
          metric_id: filter.metricId,
          operator: filter.operator,
          value: filter.value,
          value_type: filter.valueType,
        })),
      },
    }),
    [
      activeMetricValueFilters,
      includeAnyResultMetric,
      metricId,
      monitoringFilter,
    ],
  )

  return useTransportQuery({
    operation: QUERY_UPLOADED_CONVERSATION_KEYSET,
    request,
    options: {
      select: (response) => ({
        items: response.items.map((row) =>
          normalizeConversationTransportRow(row),
        ),
        nextCursor: response.next_cursor ?? undefined,
      }),
    },
  })
}

export function useSimulationOutputLookupTransport() {
  const transportUtils = useTransportQueryUtils()

  return useMemo(
    () => ({
      getSimulationOutput: (simulationOutputId: string) =>
        transportUtils.fetch("getSimulationOutput", {
          simulation_output_id: simulationOutputId,
        }),
    }),
    [transportUtils],
  )
}
